// page-backlog-reviews.ts — Revues de backlog : liste (R1) et cadrage (R2).
//
// L'écran de la revue elle-même (R3) vit dans `page-backlog-review.ts` : les
// deux modules sont indépendants, la liste n'a besoin de rien de l'arbitrage.
//
// Rien ici n'écrit dans le backlog. Une revue se crée, se liste, s'ouvre — les
// écritures métier passent par les écrans d'action existants.

import { navigateTo } from '../router.js';

export interface ReviewRow {
  id: number;
  // NULL sur les revues ouvertes avant le 2026-08-26 : la colonne est arrivée
  // après elles, et leur fabriquer un titre depuis le périmètre créerait un
  // titre que personne n'a écrit. L'affichage retombe sur le libellé.
  title: string | null;
  scope_type: string;
  scope_ids: number[];
  label: string;
  status: string;
  created_at: string | null;
  created_by_trigram: string | null;
  closed_at: string | null;
  recommendations_total: number;
  recommendations_treated: number;
}

type SortKey =
  | 'id'
  | 'title'
  | 'label'
  | 'created_at'
  | 'created_by_trigram'
  | 'recommendations_treated'
  | 'status';

let _rows: ReviewRow[] = [];
let _sortKey: SortKey = 'created_at';
let _sortDir: 'asc' | 'desc' = 'desc';

// Les six périmètres de la spec. `global` n'attend aucun objet ; les autres en
// exigent exactement un.
const SCOPES: Array<{ code: string; labelKey: string; loader?: () => Promise<unknown> }> = [
  { code: 'global', labelKey: 'review.scope.global' },
  { code: 'project', labelKey: 'review.scope.project', loader: () => api.getProjects() },
  { code: 'program', labelKey: 'review.scope.program', loader: () => api.getPrograms() },
  { code: 'structure', labelKey: 'review.scope.structure', loader: () => api.getStructures() },
  { code: 'plan', labelKey: 'review.scope.plan', loader: () => api.getActionPlans() },
];

export function fmtDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
}

/** La valeur sur laquelle trier, pour la colonne demandée.
 *
 * Le tri porte sur ce que l'utilisateur VOIT : une revue sans titre affiche son
 * périmètre, elle doit donc se trier avec lui plutôt que d'échouer en bloc au
 * début de la liste avec toutes les autres sans titre.
 */
function _sortValue(row: ReviewRow): string | number {
  if (_sortKey === 'title') return (row.title || row.label).toLocaleLowerCase('fr');
  const raw = row[_sortKey];
  if (typeof raw === 'number') return raw;
  return (raw ?? '').toLocaleLowerCase('fr');
}

function _sorted(): ReviewRow[] {
  const dir = _sortDir === 'asc' ? 1 : -1;
  return [..._rows].sort((a, b) => {
    const va = _sortValue(a);
    const vb = _sortValue(b);
    // L'ID départage : à valeur égale l'ordre ne doit jamais dépendre de
    // l'ordre d'arrivée, sinon la liste se réordonne toute seule au rechargement.
    if (va === vb) return a.id - b.id;
    return va > vb ? dir : -dir;
  });
}

function _th(key: SortKey, label: string): string {
  const arrow = _sortKey === key ? (_sortDir === 'asc' ? '▲' : '▼') : '';
  return `<th class="sortable" data-sort="${key}">${esc(label)} <span style="font-size:10px;color:var(--text3)">${arrow}</span></th>`;
}

function _progressCell(row: ReviewRow): string {
  const total = row.recommendations_total;
  if (!total) return '<td>—</td>';
  const pct = Math.round((100 * row.recommendations_treated) / total);
  const complete =
    pct >= 100 ? ` <span class="badge badge-active">${esc(t('review.list.complete'))}</span>` : '';
  return `<td>${row.recommendations_treated}/${total}${complete}
    <div style="height:3px;background:var(--bg3);border-radius:2px;margin-top:4px">
      <div style="height:3px;width:${pct}%;background:var(--ok);border-radius:2px"></div>
    </div></td>`;
}

function _statusCell(row: ReviewRow): string {
  const closed = row.status === 'closed';
  const cls = closed ? 'badge-active' : 'badge-warning';
  const label = closed ? t('review.status.closed') : t('review.status.open');
  return `<td><span class="badge ${cls}">${esc(label)}</span></td>`;
}

function _renderList(): void {
  if (!_rows.length) {
    setContent(`<div class="card"><div class="empty">${esc(t('review.list.empty'))}</div></div>`);
    return;
  }
  // Seules l'ID et le titre ouvrent la revue. Le reste de la ligne reste
  // sélectionnable, ce qui permet de copier un trigramme ou une date.
  const rows = _sorted()
    .map(
      (r) => `<tr>
        <td class="cursor-pointer text-mono" data-open="${r.id}">${r.id}</td>
        <td class="cursor-pointer" data-open="${r.id}"><strong>${esc(r.title || r.label)}</strong></td>
        <td>${esc(r.label)}</td>
        <td>${esc(fmtDay(r.created_at))}</td>
        <td>${esc(r.created_by_trigram || '—')}</td>
        ${_progressCell(r)}
        ${_statusCell(r)}
        <td style="white-space:nowrap">
          <button class="btn btn-secondary btn-icon" data-edit="${r.id}"
            aria-label="${esc(t('common.edit'))}" title="${esc(t('common.edit'))}">✎</button>
          <button class="btn btn-danger btn-icon ml-1" data-del="${r.id}"
            aria-label="${esc(t('common.delete'))}" title="${esc(t('common.delete'))}">✕</button>
        </td>
      </tr>`,
    )
    .join('');
  setContent(`<div class="card"><div class="table-wrap"><table>
      <thead><tr>
        ${_th('id', t('review.col.id'))}
        ${_th('title', t('review.col.title'))}
        ${_th('label', t('review.col.scope'))}
        ${_th('created_at', t('review.col.openedAt'))}
        ${_th('created_by_trigram', t('review.col.by'))}
        ${_th('recommendations_treated', t('review.col.treated'))}
        ${_th('status', t('review.col.status'))}
        <th></th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div></div>`);

  document.querySelectorAll<HTMLElement>('td[data-open]').forEach((td) => {
    td.addEventListener('click', () =>
      navigateTo('review/:id', { id: Number(td.dataset.open ?? 0) }),
    );
  });
  document.querySelectorAll<HTMLElement>('[data-edit]').forEach((btn) => {
    btn.addEventListener('click', () =>
      navigateTo('review/:id', { id: Number(btn.dataset.edit ?? 0) }),
    );
  });
  document.querySelectorAll<HTMLElement>('[data-del]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = Number(btn.dataset.del ?? 0);
      const row = _rows.find((r) => r.id === id);
      if (!row) return;
      // Ce qui part est ANNONCÉ avant de demander confirmation : des heures de
      // tri disparaissent ici, et c'est irréversible. Ce que la revue a fait au
      // référentiel, en revanche, ne bouge pas — elle n'y écrit jamais.
      openConfirmDangerModal(
        t('review.delete.title'),
        esc(t('review.delete.body', { name: row.title || row.label })) +
          '<br><br>' +
          esc(t('review.delete.keeps')),
        async () => {
          await api.deleteBacklogReview(id);
          _rows = _rows.filter((r) => r.id !== id);
          _renderList();
          toast(t('review.delete.done'));
        },
      );
    });
  });
  document.querySelectorAll<HTMLElement>('th[data-sort]').forEach((th) => {
    th.addEventListener('click', () => {
      const key = th.dataset.sort as SortKey;
      if (key === _sortKey) _sortDir = _sortDir === 'asc' ? 'desc' : 'asc';
      else {
        _sortKey = key;
        _sortDir = 'asc';
      }
      _renderList();
    });
  });
}

// ── R2 : cadrage d'une nouvelle revue ────────────────────────────────────────

interface ScopeOption {
  id: number;
  code?: string | null;
  name?: string | null;
  title?: string | null;
  label?: string | null;
}

function _optionLabel(o: ScopeOption): string {
  const name = o.name || o.title || o.label || '';
  return o.code ? `${o.code} — ${name}` : name;
}

/** Dernière revue connue sur ce périmètre, lue dans la liste déjà chargée.
 *
 * L'encart de contexte de la spec annonce aussi le VOLUME d'actions du
 * périmètre ; il attend un endpoint de pré-comptage qui n'existe pas encore.
 * Mieux vaut n'afficher que ce qu'on sait vraiment.
 */
function _lastReviewHint(scopeType: string, scopeId: number | null): string {
  const match = _sorted().find(
    (r) => r.scope_type === scopeType && (scopeId === null || r.scope_ids[0] === scopeId),
  );
  if (!match) return esc(t('review.new.noPrevious'));
  return esc(
    t('review.new.previous', {
      date: fmtDay(match.created_at),
      by: match.created_by_trigram || '—',
    }),
  );
}

function _scopeBody(): string {
  const radios = SCOPES.map(
    (s, i) =>
      `<label style="display:inline-flex;align-items:center;gap:6px;margin-right:16px">
        <input type="radio" name="scope-type" value="${s.code}"${i === 0 ? ' checked' : ''}>
        ${esc(t(s.labelKey))}
      </label>`,
  ).join('');
  return `<div class="form-group">
      <label for="review-title">${esc(t('review.new.titleLabel'))}</label>
      <input type="text" id="review-title" maxlength="200"
        placeholder="${esc(t('review.new.titlePlaceholder'))}">
    </div>
    <fieldset style="border:0;padding:0;margin:0">
      <legend class="form-label">${esc(t('review.new.scopeLegend'))}</legend>
      <div style="margin-bottom:12px">${radios}</div>
    </fieldset>
    <div class="form-group" id="scope-object-group" style="display:none">
      <label for="scope-object">${esc(t('review.new.objectLabel'))}</label>
      <select id="scope-object"></select>
    </div>
    <div class="card" id="scope-context" style="padding:10px;margin-bottom:12px"></div>
    <label style="display:flex;align-items:center;gap:8px">
      <input type="checkbox" id="limit-recos" checked>
      ${esc(t('review.new.limit'))}
    </label>`;
}

async function _fillScopeObjects(scopeType: string): Promise<void> {
  const group = document.getElementById('scope-object-group');
  const select = document.getElementById('scope-object') as HTMLSelectElement | null;
  const context = document.getElementById('scope-context');
  const scope = SCOPES.find((s) => s.code === scopeType);
  if (!group || !select) return;
  if (!scope?.loader) {
    group.style.display = 'none';
    if (context) context.innerHTML = _lastReviewHint(scopeType, null);
    return;
  }
  group.style.display = '';
  select.innerHTML = `<option>${esc(t('common.loading'))}</option>`;
  const items = ((await scope.loader()) as ScopeOption[]) || [];
  select.innerHTML = items
    .map((o) => `<option value="${o.id}">${esc(_optionLabel(o))}</option>`)
    .join('');
  const refresh = (): void => {
    if (context) context.innerHTML = _lastReviewHint(scopeType, Number(select.value || 0));
  };
  select.addEventListener('change', refresh);
  refresh();
}

/** Envoie le cadrage saisi dans la page, puis ouvre la revue créée. */
async function _submitCreation(btn: HTMLButtonElement | null): Promise<void> {
  const type =
    (document.querySelector('input[name="scope-type"]:checked') as HTMLInputElement | null)
      ?.value || 'global';
  const select = document.getElementById('scope-object') as HTMLSelectElement | null;
  const limit = (document.getElementById('limit-recos') as HTMLInputElement | null)?.checked;
  const scopeIds = type === 'global' ? [] : [Number(select?.value || 0)];
  if (type !== 'global' && !scopeIds[0]) {
    toast(t('review.new.objectRequired'), 'error');
    return;
  }
  const title =
    (document.getElementById('review-title') as HTMLInputElement | null)?.value.trim() || '';
  await api.withButton(btn, async () => {
    try {
      const created = (await api.createBacklogReview({
        scope_type: type,
        scope_ids: scopeIds,
        limit_recommendations: limit !== false,
        ...(title ? { title } : {}),
      })) as ReviewRow;
      // La création ne calcule rien : on arrive sur une revue vide, qui attend
      // que l'utilisateur lance la détection.
      navigateTo('review/:id', { id: created.id });
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : t('common.error'), 'error');
    }
  });
}

/** Le cadrage d'une nouvelle revue, DANS la page — `#review/0`.
 *
 * Même UX que `#meeting/0` (demande Christophe, 2026-08-26) : plus de popup
 * préalable. On cadre en pleine page, et la revue n'existe en base qu'au clic
 * sur « Créer la revue ». Rien n'est écrit tant qu'on n'a pas validé, donc
 * quitter la page ne laisse aucune revue fantôme derrière soi.
 *
 * La liste est chargée d'abord : l'encart de contexte annonce la dernière revue
 * connue sur le périmètre choisi, et il la lit dedans. Sans elle, arriver
 * directement sur `#review/0` par l'URL afficherait « aucune revue précédente »
 * à tort.
 */
export async function renderReviewCreation(preset?: { scope_type: string }): Promise<void> {
  setHeader(
    t('review.new.title'),
    `<a class="btn btn-secondary" href="#reviews">${esc(t('common.cancel'))}</a>
     <button class="btn btn-primary" id="btn-create-review">${esc(t('review.new.confirm'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  if (!_rows.length) {
    try {
      _rows = (await api.getBacklogReviews()) as ReviewRow[];
    } catch {
      // L'encart de contexte est un confort : son absence ne doit pas empêcher
      // de cadrer une revue.
      _rows = [];
    }
  }
  setContent(`<div class="card" style="max-width:760px">${_scopeBody()}</div>`);

  document.querySelectorAll<HTMLInputElement>('input[name="scope-type"]').forEach((radio) => {
    radio.addEventListener('change', () => void _fillScopeObjects(radio.value));
  });
  if (preset) {
    const radio = document.querySelector<HTMLInputElement>(
      `input[name="scope-type"][value="${preset.scope_type}"]`,
    );
    if (radio) radio.checked = true;
  }
  document.getElementById('btn-create-review')?.addEventListener('click', (e) => {
    void _submitCreation(e.currentTarget as HTMLButtonElement);
  });
  await _fillScopeObjects(preset?.scope_type || 'global');
}

// ── R1 : la liste ────────────────────────────────────────────────────────────

pages.backlogReviews = async function () {
  setHeader(
    t('review.list.title'),
    `<button class="btn btn-primary" id="btn-new-review">${esc(t('review.list.new'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    _rows = (await api.getBacklogReviews()) as ReviewRow[];
    _renderList();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
  document
    .getElementById('btn-new-review')
    ?.addEventListener('click', () => navigateTo('review/:id', { id: 0 }));
};
